import { useEffect, useState } from "react";
import CreditPurchaseModal from "../commonModel/CreditPurchaseModal";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import storage from "../../util/storage";
import ROUTES from "../../constants/routes";

import "./OverageBanner.scss";

/*
 * THE OVERAGE LADDER: the in-app half.
 *
 * Backend: SubscriptionApi/Helper/overage.ts. The short version: hitting the
 * page-view limit no longer stops tracking. It opens a FREE 24-hour buffer,
 * tracking continues, and this banner is what tells the customer that is
 * happening.
 *
 * WHY IT EXISTS AT ALL, from Dana's interview (docs/PERSONAS.md): "the hard
 * stop fails at peak value - the 100% notice arrives simultaneously with
 * lockout, on the spike day that matters most." A silent buffer would be only
 * half a fix; if nobody knows they are inside one, the lockout at the end is
 * just as much of a surprise as the old hard stop was.
 *
 * ─── ESCALATION ─────────────────────────────────────────────────────────────
 *
 * The banner gets more urgent as the buffer drains, and the last stage is NOT
 * DISMISSIBLE. That is deliberate: with under six hours left, tracking is
 * about to stop, and a banner someone dismissed yesterday must not be the
 * reason they miss it.
 *
 * Dismissal is keyed by STAGE, matching AccountIssueBanner's own reasoning -
 * dismissing the 24-hour notice must not silently suppress the 6-hour one.
 */

type Stage = "early" | "mid" | "urgent" | "expired";

const DISMISSED_STAGE_KEY = "overageBannerDismissedStage";

// Below this, the banner can no longer be dismissed.
const URGENT_HOURS = 6;
const MID_HOURS = 12;

const stageFor = (hoursRemaining: number | null, inBuffer: boolean): Stage | null => {
  // Over the limit with no buffer left: tracking has stopped. This is the one
  // state the old hard stop produced immediately; now it is the last rung.
  if (!inBuffer) {
    return "expired";
  }
  if (hoursRemaining == null) {
    return null;
  }
  if (hoursRemaining <= URGENT_HOURS) {
    return "urgent";
  }
  if (hoursRemaining <= MID_HOURS) {
    return "mid";
  }

  return "early";
};

const OverageBanner = () => {
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);
  const overage = authReducer?.accountRecord?.overage ?? null;

  const [dismissedStage, setDismissedStage] = useState<string | null>(() =>
    storage.get(DISMISSED_STAGE_KEY)
  );

  /*
   * "Add page views" used to be a <Link> to Settings. On Settings itself that
   * is a no-op: the customer clicks the one action the banner offers and
   * nothing happens. The banner is a system message shown on EVERY page, so
   * its action has to work on every page, which means opening the purchase
   * modal directly rather than navigating somewhere that might already be
   * where you are.
   */
  const [showCreditPurchase, setShowCreditPurchase] = useState(false);

  /*
   * The countdown has to run CLIENT-SIDE (2026-08-27).
   *
   * hoursRemaining arrives computed by the server at fetch time. On its own
   * that means the banner freezes on whatever it said when the page loaded:
   * it would still read "3 more hours" three hours later, and would keep
   * saying tracking was fine after the buffer had actually expired and
   * tracking had stopped. A countdown that does not count down is worse than
   * no countdown, because it is confidently wrong at exactly the moment the
   * customer needs it.
   *
   * bufferExpiresAt is an absolute timestamp we already have, so the remaining
   * time is derived from it locally and re-derived every minute. The banner
   * escalates through mid -> urgent -> expired on its own, with no refresh.
   */
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // No overage state at all, or comfortably under the limit - nothing to say.
  if (!overage || (!overage.inBuffer && !overage.bufferExpiresAt)) {
    return null;
  }

  const expiresAt: number | null = overage.bufferExpiresAt ?? null;
  const liveHoursRemaining =
    expiresAt != null ? (expiresAt - now) / (1000 * 60 * 60) : overage.hoursRemaining;
  const liveInBuffer = expiresAt != null ? now < expiresAt : overage.inBuffer;

  const stage = stageFor(liveHoursRemaining, liveInBuffer);

  if (!stage) {
    return null;
  }

  // Urgent and expired are never dismissible - see the header note.
  const dismissible = stage === "early" || stage === "mid";

  if (dismissible && stage === dismissedStage) {
    return null;
  }

  const handleDismiss = () => {
    storage.set(DISMISSED_STAGE_KEY, stage);
    setDismissedStage(stage);
  };

  const hours = Math.max(0, Math.ceil(liveHoursRemaining ?? 0));
  const settingsRoute = ROUTES.SETTINGS.replace(":id", "");

  /*
   * The copy is deliberately NOT alarmed in the early stages. The customer has
   * done nothing wrong - they had a good day - and tracking is still running.
   * Leading with "still tracking" before the countdown is the whole difference
   * between this and the hard stop it replaces.
   */
  const message =
    stage === "expired" ? (
      <>
        You're over your page-view limit and tracking has paused.{" "}
        <button type="button" className="overage-banner-action" onClick={() => setShowCreditPurchase(true)}>Add page views</button> to start again.
      </>
    ) : (
      <>
        You've passed your page-view limit. <strong>Still tracking</strong>, free, for{" "}
        <strong>
          {hours} more hour{hours === 1 ? "" : "s"}
        </strong>
        . <button type="button" className="overage-banner-action" onClick={() => setShowCreditPurchase(true)}>Add page views</button> to keep going.
      </>
    );

  return (
    <>
    <div className={`overage-banner overage-banner--${stage}`}>
      <p>{message}</p>
      {dismissible && (
        <button
          type="button"
          className="overage-banner-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss"
        >
          &times;
        </button>
      )}
    </div>

    {showCreditPurchase && (
      <CreditPurchaseModal onClose={() => setShowCreditPurchase(false)} />
    )}
    </>
  );
};

export default OverageBanner;
